/**
 * Ranking Utility Functions
 * 
 * Provides helper functions for calculating team ranks with proper tie handling.
 * Teams with the same score should have the same rank.
 */

/**
 * Calculate ranks for teams based on their scores
 * Teams with the same score get the same rank (tied)
 * @param {Array} teams - Array of team objects with score property
 * @param {Function} getScore - Function to get score from team object (optional, defaults to team.score)
 * @returns {Array} Array of teams with rank property added
 */
export function calculateRanks(teams, getScore = (team) => team.score) {
  if (!teams || teams.length === 0) return [];
  
  // Sort teams by score (descending)
  const sortedTeams = [...teams]
    .map(team => ({
      ...team,
      score: getScore(team)
    }))
    .sort((a, b) => b.score - a.score);
  
  // Calculate positions (teams with same score get same position)
  let currentPosition = 1;
  const teamsWithPositions = sortedTeams.map((team, index) => {
    // If this team has a different score than the previous team, update position
    if (index > 0 && sortedTeams[index - 1].score !== team.score) {
      currentPosition = index + 1;
    }
    return {
      ...team,
      rank: currentPosition
    };
  });
  
  return teamsWithPositions;
}

/**
 * Get rank for a specific team
 * @param {string} teamId - Team ID
 * @param {Array} teams - Array of all teams
 * @param {Function} getScore - Function to get score from team object (optional)
 * @returns {number} Rank of the team (1-based)
 */
export function getTeamRank(teamId, teams, getScore = (team) => team.score) {
  const rankedTeams = calculateRanks(teams, getScore);
  const team = rankedTeams.find(t => t.id === teamId);
  return team ? team.rank : null;
}

